import type { ApiResponse, StatusResponse, CheckInResponse, SystemConfig } from "@dmtrigger/shared";

// Get API base URL with fallback for different environments
const getApiBaseUrl = (): string => {
  // Check for environment variable (production deployment)
  if (typeof window !== "undefined" && (window as any).__API_BASE_URL__) {
    return (window as any).__API_BASE_URL__;
  }

  // Check for Vite environment variable
  if ((import.meta as any).env?.VITE_API_BASE_URL) {
    return (import.meta as any).env.VITE_API_BASE_URL as string;
  }

  // Default to localhost for development
  return "http://localhost:8787";
};

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  private baseUrl: string;
  private getAccessToken: () => Promise<string>;

  constructor(baseUrl: string = API_BASE_URL, getAccessToken?: () => Promise<string>) {
    this.baseUrl = baseUrl;
    this.getAccessToken = getAccessToken || (() => Promise.resolve(""));
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get Auth0 access token
    let token = "";
    try {
      token = await this.getAccessToken();
    } catch (error) {
      console.warn("Failed to get access token:", error);
    }

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getStatus(): Promise<StatusResponse> {
    const response = await this.request<StatusResponse>("/api/v1/status");
    return response.data!;
  }

  async checkIn(metadata?: Record<string, any>): Promise<CheckInResponse> {
    const response = await this.request<CheckInResponse>("/api/v1/checkin", {
      method: "POST",
      body: JSON.stringify({ metadata }),
    });
    return response.data!;
  }

  async getConfig(): Promise<SystemConfig> {
    const response = await this.request<SystemConfig>("/api/v1/config");
    return response.data!;
  }

  async getAuditLog(limit = 50, offset = 0) {
    const response = await this.request(`/api/v1/audit?limit=${limit}&offset=${offset}`);
    return response.data as { entries: any[]; pagination: any };
  }

  // API Key management
  async createApiKey(name: string) {
    const response = await this.request("/api/v1/api-keys", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    return response.data;
  }

  async getApiKeys() {
    const response = await this.request("/api/v1/api-keys");
    return response.data;
  }

  async deleteApiKey(keyId: string) {
    const response = await this.request(`/api/v1/api-keys/${keyId}`, {
      method: "DELETE",
    });
    return response.data;
  }

  // Generic HTTP methods
  async get(endpoint: string) {
    return this.request(endpoint);
  }

  async post(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint: string) {
    return this.request(endpoint, {
      method: "DELETE",
    });
  }

  // Health check (doesn't require auth)
  async healthCheck() {
    const response = await this.request("/health");
    return response.data;
  }
}

// Create API client with Auth0 token provider
export function createApiClient(getAccessToken: () => Promise<string>) {
  return new ApiClient(API_BASE_URL, getAccessToken);
}

// Default API client (for health checks and unauthenticated endpoints)
export const apiClient = new ApiClient();
